import React, { useState, type ReactElement, type ReactNode } from "react";
import {
  Bell,
  ChevronUp,
  LogOut,
  Palette,
  Settings,
  type LucideIcon,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  cn,
} from "@ndd/ui";
import { avatarClass, initials } from "../../lib/avatar";
import type { ShellLookSwitch } from "./shell-header";
import { SiderailLabelClip, SiderailTooltip } from "./siderail-parts";

// The rail shell's account menu (NO-754): who is signed in, at the foot of the
// rail, as one button that opens a menu of Notifications (NO-834: the
// viewer's notification settings, while the `notifications` flag is on),
// Admin settings (admins only), the look switch (while the flag offers it)
// and Sign out. A leaf module (`import
// React`, relative imports, no router, no Clerk) so `tsx --test` renders it;
// the composer (rail-account.tsx) supplies Clerk's image and sign-out and a
// real link to the Admin area. The classic shell keeps Clerk's UserButton.

export interface RailAccount {
  id: string;
  name: string;
  email: string;
  roleLine: string;
  /** Clerk's profile image, the one its UserButton shows; initials without it. */
  imageUrl?: string;
  /**
   * False when imageUrl is Clerk's generated placeholder rather than a photo
   * the person uploaded. Under the always theme the placeholder gives way to
   * lime initials (D3); a real photo always shows.
   */
  hasImage?: boolean;
}

export type RailAccountItem =
  | "notification-settings"
  | "admin-settings"
  | "look"
  | "sign-out";

/** The menu's items under the name and email header, in order. */
export function railAccountItems({
  notificationSettings = false,
  adminSettings,
  lookSwitch,
}: {
  notificationSettings?: boolean;
  adminSettings: boolean;
  lookSwitch?: ShellLookSwitch;
}): RailAccountItem[] {
  const items: RailAccountItem[] = [];
  if (notificationSettings) items.push("notification-settings");

  if (adminSettings) items.push("admin-settings");

  if (lookSwitch) items.push("look");

  items.push("sign-out");

  return items;
}

export function RailAccountAvatar({
  account,
  className,
}: {
  account: RailAccount;
  className?: string;
}): ReactElement {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const imageUrl = account.imageUrl === failedUrl ? undefined : account.imageUrl;
  const frame = cn("grid shrink-0 place-items-center rounded-full", className);
  const placeholder = account.hasImage === false;
  const initialsBadge = (themeOnly: boolean) => (
    <span
      aria-hidden="true"
      className={cn(
        frame,
        "font-sans text-caption font-bold text-white",
        avatarClass(account.id),
        themeOnly && "hidden brand:grid"
      )}
    >
      {initials(account.name)}
    </span>
  );

  if (!imageUrl) return initialsBadge(false);

  return (
    <>
      <img
        src={imageUrl}
        alt=""
        aria-hidden="true"
        onError={() => setFailedUrl(imageUrl)}
        className={cn(frame, "object-cover", placeholder && "brand:hidden")}
      />
      {placeholder ? initialsBadge(true) : null}
    </>
  );
}

export type RenderAdminSettingsLink = (props: {
  className: string;
  children: ReactNode;
}) => ReactElement;

export interface RailAccountMenuProps {
  account: RailAccount;
  /** The Notifications item, to the Notification settings page (NO-834). */
  notificationSettings?: boolean;
  renderNotificationSettingsLink?: RenderAdminSettingsLink;
  adminSettings: boolean;
  lookSwitch?: ShellLookSwitch;
  onSignOut: () => void;
  renderAdminLink: RenderAdminSettingsLink;
}

const ITEM_CLASS =
  "gap-2.5 rounded-lg px-2.5 py-2 font-sans text-body text-foreground focus:bg-muted [&_svg]:text-muted-foreground";

function ItemLabel({
  icon: Icon,
  label,
}: {
  icon: LucideIcon;
  label: string;
}): ReactElement {
  return (
    <>
      <Icon aria-hidden="true" />
      {label}
    </>
  );
}

function RailAccountMenuItem({
  item,
  menu,
}: {
  item: RailAccountItem;
  menu: RailAccountMenuProps;
}): ReactElement | null {
  if (item === "notification-settings") {
    if (!menu.renderNotificationSettingsLink) return null;

    return (
      <DropdownMenuItem asChild className={ITEM_CLASS}>
        {menu.renderNotificationSettingsLink({
          className: ITEM_CLASS,
          children: <ItemLabel icon={Bell} label="Notifications" />,
        })}
      </DropdownMenuItem>
    );
  }

  if (item === "admin-settings") {
    return (
      <DropdownMenuItem asChild className={ITEM_CLASS}>
        {menu.renderAdminLink({
          className: ITEM_CLASS,
          children: <ItemLabel icon={Settings} label="Admin settings" />,
        })}
      </DropdownMenuItem>
    );
  }

  if (item === "look" && menu.lookSwitch) {
    return (
      <DropdownMenuItem className={ITEM_CLASS} onSelect={menu.lookSwitch.onSelect}>
        <ItemLabel icon={Palette} label={menu.lookSwitch.label} />
      </DropdownMenuItem>
    );
  }

  return (
    <DropdownMenuItem className={ITEM_CLASS} onSelect={menu.onSignOut}>
      <ItemLabel icon={LogOut} label="Sign out" />
    </DropdownMenuItem>
  );
}

function RailAccountMenuContent({
  menu,
  side,
  align,
  className,
}: {
  menu: RailAccountMenuProps;
  side: "top" | "right" | "bottom";
  align: "start" | "end";
  className?: string;
}): ReactElement {
  const items = railAccountItems(menu);

  return (
    <DropdownMenuContent
      side={side}
      align={align}
      sideOffset={8}
      className={cn(
        "min-w-[232px] rounded-xl border-border p-1.5 shadow-lg",
        className
      )}
    >
      <DropdownMenuLabel className="px-2.5 py-2 font-normal">
        <span className="block truncate font-sans text-body font-bold text-foreground">
          {menu.account.name}
        </span>
        <span className="block truncate text-meta text-muted-foreground">
          {menu.account.email}
        </span>
      </DropdownMenuLabel>
      <DropdownMenuSeparator className="bg-border" />
      {items.map((item) => (
        <React.Fragment key={item}>
          {item === "sign-out" && items.length > 1 ? (
            <DropdownMenuSeparator className="bg-border" />
          ) : null}
          <RailAccountMenuItem item={item} menu={menu} />
        </React.Fragment>
      ))}
    </DropdownMenuContent>
  );
}

/**
 * The account row at the rail's foot: avatar, name and role, the whole row one
 * button. In the icon strip only the avatar shows, named by its tooltip, and
 * the menu opens to its right; otherwise it opens above the row, as wide.
 */
export function RailAccountRow({
  menu,
  collapsed,
  opensRight,
}: {
  menu: RailAccountMenuProps;
  collapsed: boolean;
  /** The desktop icon strip: the menu opens beside the avatar. */
  opensRight: boolean;
}): ReactElement {
  const { account } = menu;

  return (
    <DropdownMenu>
      <SiderailTooltip label={account.name} collapsed={collapsed}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={account.name}
            className="flex h-12 w-full items-center rounded-lg text-left outline-none transition-colors hover:bg-accent focus-visible:ring-1 focus-visible:ring-ring data-[state=open]:bg-accent"
          >
            <span className="grid size-10 shrink-0 place-items-center">
              <RailAccountAvatar account={account} className="size-8" />
            </span>
            <SiderailLabelClip collapsed={collapsed}>
              <span className="flex items-center">
                <span className="min-w-0 flex-1 pl-1">
                  <span className="block truncate font-sans text-body font-bold text-foreground">
                    {account.name}
                  </span>
                  <span className="block truncate text-meta text-muted-foreground">
                    {account.roleLine}
                  </span>
                </span>
                <ChevronUp aria-hidden="true" className="mx-2 size-3.5 text-subtle" />
              </span>
            </SiderailLabelClip>
          </button>
        </DropdownMenuTrigger>
      </SiderailTooltip>
      <RailAccountMenuContent
        menu={menu}
        side={opensRight ? "right" : "top"}
        align={opensRight ? "end" : "start"}
        className={
          opensRight ? undefined : "w-[var(--radix-dropdown-menu-trigger-width)]"
        }
      />
    </DropdownMenu>
  );
}

/** The phone bar's avatar: the same menu, dropping below it. */
export function RailAccountAvatarButton({
  menu,
}: {
  menu: RailAccountMenuProps;
}): ReactElement {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={menu.account.name}
          className="grid size-9 place-items-center rounded-full outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <RailAccountAvatar account={menu.account} className="size-8" />
        </button>
      </DropdownMenuTrigger>
      <RailAccountMenuContent menu={menu} side="bottom" align="end" />
    </DropdownMenu>
  );
}
